/**
 * CommunicationsTab — Sub-tabs for emails, SMS, and internal notes
 */

import React, { useState, useMemo } from 'react';
import { useChronologicCommunications } from '@/features/admin/tabs/tracker/hooks/useChronologicCommunications';
import { useTrackerEmails } from '@/features/admin/tabs/tracker/hooks/useTrackerEmails';
import { fmtDate, fmtRelative } from '../utils/formatters';

interface CommunicationsTabProps {
  affaireId: number;
}

type CommSubTab = 'emails' | 'sms' | 'notes';

export const CommunicationsTab: React.FC<CommunicationsTabProps> = ({ affaireId }) => {
  const [subTab, setSubTab] = useState<CommSubTab>('emails');

  const { communications, isLoading: commsLoading } = useChronologicCommunications(affaireId);
  const { data: emailsData, isLoading: emailsLoading } = useTrackerEmails(affaireId);
  const emails = emailsData?.data || [];

  // Filter communications by type
  const internalNotes = useMemo(() =>
    communications.filter(c =>
      c.type === 'tracker_com' || c.type === 'com_internes' || c.type === 'com_post_it' || c.type === 'com_avant_envoyer'
    ).sort((a, b) => new Date(b.event_date).getTime() - new Date(a.event_date).getTime()),
    [communications]
  );

  const smsMessages = useMemo(() =>
    communications.filter(c => c.type === 'sms_outbox' || c.type === 'sms_inbox')
      .sort((a, b) => new Date(b.event_date).getTime() - new Date(a.event_date).getTime()),
    [communications]
  );

  const isLoading = commsLoading || emailsLoading;

  return (
    <div className="aff-detail-comms">
      {/* Sub-tab bar */}
      <div className="aff-detail-comms-subtabs">
        <button
          className={`aff-detail-comms-subtab ${subTab === 'emails' ? 'active' : ''}`}
          onClick={() => setSubTab('emails')}
        >
          Emails <span className="aff-detail-comms-count">{emails.length}</span>
        </button>
        <button
          className={`aff-detail-comms-subtab ${subTab === 'sms' ? 'active' : ''}`}
          onClick={() => setSubTab('sms')}
        >
          SMS <span className="aff-detail-comms-count">{smsMessages.length}</span>
        </button>
        <button
          className={`aff-detail-comms-subtab ${subTab === 'notes' ? 'active' : ''}`}
          onClick={() => setSubTab('notes')}
        >
          Notes internes <span className="aff-detail-comms-count">{internalNotes.length}</span>
        </button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="aff-detail-loading" style={{ height: 200 }}>
          <div className="aff-detail-spinner" />
        </div>
      ) : (
        <div className="aff-detail-comms-content">
          {subTab === 'emails' && (
            <EmailsList emails={emails} />
          )}
          {subTab === 'sms' && (
            <MessageList items={smsMessages} emptyText="Aucun SMS" />
          )}
          {subTab === 'notes' && (
            <MessageList items={internalNotes} emptyText="Aucune note interne" />
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Email List ─── */
const EmailsList: React.FC<{ emails: any[] }> = ({ emails }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = emails.find(e => e.id === selectedId);

  if (emails.length === 0) {
    return <div className="aff-detail-card-empty">Aucun email</div>;
  }

  return (
    <div className="aff-detail-emails-split">
      {/* Email list */}
      <div className="aff-detail-emails-list">
        {emails.map(e => (
          <div
            key={e.id}
            className={`aff-detail-email-item ${selectedId === e.id ? 'active' : ''}`}
            onClick={() => setSelectedId(e.id)}
          >
            <div className="aff-detail-email-item-header">
              <span className={`aff-detail-email-direction ${e.direction === 'received' ? 'received' : 'sent'}`}>
                {e.direction === 'received' ? '↓ Reçu' : '↑ Envoyé'}
              </span>
              <span className="aff-detail-email-date">{fmtDate(e.email_time || e.created_at)}</span>
            </div>
            <div className="aff-detail-email-subject">{e.subject || '(sans sujet)'}</div>
            <div className="aff-detail-email-sender">
              {e.direction === 'received' ? `De: ${e.sender_name || e.sender_address || '?'}` : `À: ${e.recipients_display || '?'}`}
            </div>
          </div>
        ))}
      </div>

      {/* Email detail */}
      {selected && (
        <div className="aff-detail-email-detail">
          <div className="aff-detail-email-detail-header">
            <h3>{selected.subject || '(sans sujet)'}</h3>
            <button className="aff-detail-close-btn" onClick={() => setSelectedId(null)}>✕</button>
          </div>
          <div className="aff-detail-email-meta">
            <div><strong>De:</strong> {selected.sender_name || selected.sender_address || '?'}</div>
            {selected.recipients_display && <div><strong>À:</strong> {selected.recipients_display}</div>}
            <div><strong>Date:</strong> {fmtDate(selected.email_time || selected.created_at)}</div>
          </div>
          <div className="aff-detail-email-body">
            {selected.body_preview || selected.latest_reply || '(pas de contenu)'}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Message List (for SMS and Notes) ─── */
const MessageList: React.FC<{ items: any[]; emptyText: string }> = ({ items, emptyText }) => {
  if (items.length === 0) {
    return <div className="aff-detail-card-empty">{emptyText}</div>;
  }

  return (
    <div className="aff-detail-message-list">
      {items.map(item => (
        <div key={item.id} className="aff-detail-message-item">
          <div className="aff-detail-message-header">
            <span className="aff-detail-message-author">{item.author || item.type}</span>
            <span className="aff-detail-message-time">{fmtRelative(item.event_date)}</span>
          </div>
          <div className="aff-detail-message-content">{item.content || '-'}</div>
          {item.is_done != null && (
            <span className={`aff-detail-message-status ${item.is_done ? 'done' : 'open'}`}>
              {item.is_done ? '✓ Traité' : 'À traiter'}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};
